import React from 'react'
import { Image, ProgressBar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import closeimg from '@/assets/images/close-red.svg'
import docximg from '@/assets/images/file-doc.svg'
import pdfimg from '@/assets/images/file-pdf.svg'
import pptimg from '@/assets/images/file-ppt.svg'
import DragAndDrop from '@/components/UI/DragAndDrop'
import { useHelper } from '@/hooks/useHelper'

interface DocumentFile extends File {
  sizeError?: boolean
  progress?: number
  isUploading?: boolean
  isError?: boolean
  message?: string
}

interface DocumentUploadSectionProps {
  documents: DocumentFile[]
  onFilesSelected: (files: File[]) => void
  onFileRemove: (index: number) => void
  isUploading: boolean
  supportedTypes?: string
  maxFiles?: number
}

const DocumentUploadSection: React.FC<DocumentUploadSectionProps> = ({
  documents,
  onFilesSelected,
  onFileRemove,
  isUploading,
  supportedTypes = '.docx, .txt, .pdf',
  maxFiles = 1,
}) => {
  const { t } = useTranslation('translation')
  const { formatBytes } = useHelper()

  const getFileIcon = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase()
    if (extension?.includes('doc')) return docximg
    if (extension?.includes('ppt')) return pptimg
    return pdfimg
  }

  return (
    <>
      <div className="faq-upload-container mt-4 text-center mb-3">
        <DragAndDrop
          getFileDocumentsHandler={(files: File[]) => {
            if (files.length) {
              onFilesSelected(files)
            }
          }}
          supportedTypes={supportedTypes}
          message={`<h2>${t('Drop your files here or click to browse.')}</h2><div>${t('Supported File types')}: ${supportedTypes}</div>`}
          maxFiles={maxFiles}
          fileSizeMessage={t('')}
        />
      </div>

      {documents.length > 0 && (
        <div className="justify-content-center text-center">
          <div className="selected-files">
            {documents.map((doc, index) => (
              <div className="files d-flex align-items-center" key={index}>
                <Image src={getFileIcon(doc.name)} />
                <div className="mx-3">
                  <strong className={doc.sizeError ? 'f-error' : ''}>
                    {doc.name}
                  </strong>
                  {!doc.sizeError && <span>{formatBytes(doc.size)}</span>}
                  {doc.sizeError && (
                    <span className="f-error">
                      {t('File not uploaded. File size is more than 10 MB.')}
                    </span>
                  )}
                  {doc.progress !== undefined && (
                    <ProgressBar now={doc.progress} />
                  )}
                  {doc.isError && <span>{doc.message}</span>}
                </div>
                {!doc.isUploading && !isUploading && (
                  <button
                    className="btn ml-auto p-0"
                    onClick={() => onFileRemove(index)}
                  >
                    <Image src={closeimg} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

export default DocumentUploadSection
